import { useState } from "react";
import { useTranslation } from "react-i18next";
import { submitLead } from "../lib/api";
import { FORM } from "../constants/testIds";
import { CheckCircle2, AlertCircle, Send } from "lucide-react";

export default function LeadForm({ pageOrigin = "home", defaultService = "" }) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    nombre: "",
    empresa: "",
    email: "",
    telefono: "",
    servicio_interes: defaultService,
    mensaje: "",
  });
  const [state, setState] = useState({ loading: false, ok: false, err: "" });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, err: "" });
    try {
      await submitLead({
        ...form,
        idioma: i18n.language?.startsWith("es") ? "es" : "en",
        pagina_origen: pageOrigin,
      });
      setState({ loading: false, ok: true, err: "" });
      setForm({ nombre: "", empresa: "", email: "", telefono: "", servicio_interes: defaultService, mensaje: "" });
    } catch (err) {
      setState({ loading: false, ok: false, err: err?.response?.data?.detail || t("common.error") });
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl bg-background text-foreground placeholder:text-muted-foreground/60 border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition";

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-1">
        <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-muted-foreground">{t("form.name")} *</label>
        <input
          required
          name="nombre"
          value={form.nombre}
          onChange={onChange}
          data-testid={FORM.name}
          className={inputCls}
          placeholder="—"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-muted-foreground">{t("form.company")}</label>
        <input
          name="empresa"
          value={form.empresa}
          onChange={onChange}
          data-testid={FORM.company}
          className={inputCls}
          placeholder="—"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-muted-foreground">{t("form.email")} *</label>
        <input
          required
          type="email"
          name="email"
          value={form.email}
          onChange={onChange}
          data-testid={FORM.email}
          className={inputCls}
          placeholder="nombre@empresa.com"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-muted-foreground">{t("form.phone")}</label>
        <input
          name="telefono"
          value={form.telefono}
          onChange={onChange}
          data-testid={FORM.phone}
          inputMode="tel"
          pattern="^\+58\s?\d{3}\s?\d{3}\s?\d{4}$"
          title="Formato esperado: +58 412 149 6804"
          className={inputCls}
          placeholder="+58…"
        />
      </div>
      <div className="md:col-span-2">
        <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-muted-foreground">{t("form.service")}</label>
        <select
          name="servicio_interes"
          value={form.servicio_interes}
          onChange={onChange}
          data-testid={FORM.service}
          className={inputCls}
        >
          <option value="">{t("form.selectService")}</option>
          <option value="capacitaciones">{t("form.services.capacitaciones")}</option>
          <option value="conferencias">{t("form.services.conferencias")}</option>
          <option value="consultorias">{t("form.services.consultorias")}</option>
          <option value="desarrollo">{t("form.services.desarrollo")}</option>
          <option value="otro">{t("form.services.otro")}</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-muted-foreground">{t("form.message")}</label>
        <textarea
          name="mensaje"
          value={form.mensaje}
          onChange={onChange}
          data-testid={FORM.message}
          rows={5}
          className={inputCls}
          placeholder="—"
        />
      </div>

      <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
        <button
          type="submit"
          disabled={state.loading}
          data-testid={FORM.submit}
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
          {state.loading ? t("common.submitting") : t("common.submit")}
        </button>

        {state.ok && (
          <div data-testid={FORM.success} className="flex items-center gap-2 text-sm text-primary">
            <CheckCircle2 className="w-5 h-5" />
            {t("common.success")}
          </div>
        )}
        {state.err && (
          <div data-testid={FORM.error} className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="w-5 h-5" />
            {state.err}
          </div>
        )}
      </div>
    </form>
  );
}
